import {
  Box,
  Divider,
  Flex,
  Price,
  PrimaryButton,
  Spinner,
  Stack,
  Text,
  LegacyText,
  Button,
} from "flicket-ui";
import { ensurePlainTextFormat, formatPrice } from "@flicket/utils";
import { ReactNode, useState } from "react";
import { ContentModal, Icon } from "~components";
import { getAfterPayLocaleByCurrency } from "~config";
import BuyNowPayLater, {
  AfterpayTeaser,
} from "~features/seated-reservation/components/common/common.BuyNowPayLater";
import {
  MembershipsSoldForUserQuery,
  ReleaseType,
  TicketsSoldForUserQuery,
  ZonePricingLevel,
} from "~graphql/sdk";
import { ListItem } from "./non-seated.ListItem";
import {
  EventQuery,
  ImageGalleryItem,
  IntegrationType,
} from "../../../graphql/typed-document-nodes";
import {
  IReleaseTicket,
  IReleaseZone,
} from "~features/nonseated-reservation/nonseated-reservation.types";
import {
  INonSeatedProps,
  TSoldOutWaitlistMessage,
  useViewModel,
} from "~features/nonseated-reservation/components/NonSeatedList/NonSeatedList.useViewModel";
import { DetailsModal } from "~features/nonseated-reservation/components/DetailsModal/DetailsModal";
import { ImageSlider } from "~features/reservation/non-seated/ImageSlider";
import {
  PlainTextRender,
  TextPreviewRender,
} from "~features/nonseated-reservation/components/common/TextRenders/TextRenders";
import { useIntersectZone } from "~features/reservation/non-seated/useIntersection.hook";
import { hasTextSelection } from "../../../lib/helpers/textSelection";
import { ImportantNotice, R18Notice } from "./non-seated.components";
import { updateUnsyncedQueryParams } from "~lib/state-stores/appStore/action.updateUnsyncedQueryParams";
import { useTheme } from "styled-components";
import { Info } from "@phosphor-icons/react";
import { PaymentPlanBadge } from "~features/seated-reservation/components/common/common.PaymentPlanBadge";
import { Cart } from "~features/checkout/cart";
import { getImageSrc } from "~lib/helpers/getImage";
import { useOrganization } from "~hooks";

export const ReservationList = (props: INonSeatedProps) => {
  const {
    data,
    visibleZonesAndTickets,
    isExperienceSales,
    delayedIsSubmitting,
    importantNotice,
    showIsR18,
    ticketTypeVariantTitle,
    hasResaleWaitlist,
    showHiddenFees,
    isAdmin,
    selectedTickets,
    selectedAddons,
    releaseType,
    requiresAuth,
    ticketsSoldForUser,
    SoldOutWaitlistMessage,
    showWaitlistMessage,
    addonTypes,
    hasTicketSelected,
    userCredits,
    user,
    isPOS,
    paymentPlanSettings,
    isSubmitting,
    primaryButtonLabel,
    alertError,
    setAlertError,
    orgCurrency,
    isMobile,
    events: { onChangeQuantity, onOrderCreationClick, onClickZone },
    cart,
  } = useViewModel(props);

  // Combine zones + add-ons into a single array
  const allZones: IReleaseZone[] = [...visibleZonesAndTickets];

  // If we have add-ons, treat them as a "zone"
  if (addonTypes.length > 0) {
    // Hack: membership images
    if (data.type === "membership") {
      addonTypes.forEach((addonType) => {
        const imageSrc = getImageSrc(addonType.image);
        if (addonType.image && !addonType.imageGallery && imageSrc) {
          addonType.imageGallery = [
            {
              id: addonType.id,
              original: {
                src: imageSrc,
                metaData: addonType.image.metaData,
              },
              cropped: {
                src: imageSrc,
                metaData: addonType.image.metaData,
              },
            },
          ];
        }
      });
    }

    allZones.push({
      id: "add-ons",
      name: isExperienceSales ? "Add-ons" : "Select Addons",
      description: undefined,
      imageGallery: undefined,
      ticketTypes: addonTypes,
      isActive: true,
      pricingLevel: ZonePricingLevel.Zone,
      displayOrder: 0,
    });
  }

  if (delayedIsSubmitting) {
    return <DelayedSubmissionMessage />;
  }

  return (
    <Box
      maxW={{ _: "100%", md: isExperienceSales ? "none" : "640px" }}
      m={{ _: 0, md: "0 auto" as any }}
      px={isExperienceSales ? 0 : 2}
    >
      {!isExperienceSales && (
        <>
          {!isPOS && <ImportantNotice value={importantNotice} />}
          {showIsR18 && <R18Notice />}
        </>
      )}

      <Section
        isExperienceSales={isExperienceSales}
        title={
          isExperienceSales ? undefined : `Select ${ticketTypeVariantTitle}`
        }
      >
        <ZoneList
          isExperienceSales={isExperienceSales}
          zones={allZones}
          addons={addonTypes}
          hasResaleWaitlist={hasResaleWaitlist}
          showHiddenFees={showHiddenFees}
          handleChange={onChangeQuantity}
          isAdmin={isAdmin}
          selectedTickets={selectedTickets}
          selectedAddons={selectedAddons}
          releaseType={releaseType}
          requiresAuth={requiresAuth}
          ticketsSoldForUser={ticketsSoldForUser}
          SoldOutWaitlistMessage={SoldOutWaitlistMessage}
          showWaitlistMessage={showWaitlistMessage}
          onClickZone={onClickZone}
          eventId={data.type === "event" ? data.id : undefined}
          isMobile={isMobile}
        />
      </Section>

      {PaymentOptions({
        isExperienceSales,
        hasTicketSelected,
        userCredits,
        user,
        isSubmitting,
        primaryButtonLabel,
        showHiddenFees,
        orgCurrency,
        isMobile,
        onOrderCreationClick,
        showGateways: !isPOS && cart.total - userCredits > 0,
        showUsedCredits: userCredits > 0 && hasTicketSelected,
        paymentPlanSettings,
        cart,
      })}

      {!isExperienceSales && (
        <>
          <br />
          <br />
          <br />
          <br />
          <br />
        </>
      )}

      <ContentModal
        isOpen={!!alertError}
        setIsOpen={() => setAlertError(undefined)}
        title={alertError?.title}
      >
        {typeof alertError?.message === "string" ? (
          <div
            dangerouslySetInnerHTML={{
              __html: alertError?.message,
            }}
          />
        ) : (
          alertError?.message
        )}
      </ContentModal>
    </Box>
  );
};

function Section({
  id,
  isExperienceSales,
  title,
  children,
}: {
  id?: string;
  isExperienceSales: boolean;
  title: ReactNode;
  children?: ReactNode;
}) {
  const TitleComponent = (() => {
    if (isExperienceSales) {
      return <Text variant="header.M">{title}</Text>;
    }
    return (
      <LegacyText
        color="N800"
        fontWeight="heavy"
        fontSize={{ _: 6, md: 7 }}
        textAlign={{ _: "center", md: "center" }}
        lineHeight={{ _: "normal", md: "high" }}
        marginTop={{ _: 3, md: 0 }}
      >
        {title}
      </LegacyText>
    );
  })();

  return (
    <Box id={id} mt={isExperienceSales ? 0 : 3}>
      <Stack gap={isExperienceSales ? 0 : 2} direction="vertical" mb={3}>
        {title && TitleComponent}
        {children}
      </Stack>
    </Box>
  );
}

function Group({
  id,
  isExperienceSales,
  title,
  description,
  imageGallery,
  children,
  isFirst = false,
  isLast = false,
  isPOSRelease,
}: {
  id: string;
  isExperienceSales: boolean;
  title: string;
  description?: string;
  onClick?: () => void;
  children?: ReactNode;
  imageGallery: ImageGalleryItem[];
  hasTicketTypes: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isPOSRelease: boolean;
}) {
  const theme = useTheme();

  const { ref: titleComponentRef } = useIntersectZone({
    onIntersectTop: () => {
      updateUnsyncedQueryParams({ zoneId: id });
    },
  });

  const { ref: headSpacerRef } = useIntersectZone({
    onIntersectTop: () => {
      updateUnsyncedQueryParams({ zoneId: id });
    },
  });

  const TitleComponent = (() => {
    if (isExperienceSales) {
      const canClick =
        imageGallery?.length > 0 ||
        ensurePlainTextFormat(description)?.length > 0;

      return (
        <Box
          ref={titleComponentRef}
          cursor={canClick ? "pointer" : "auto"}
          onClick={() => {
            if (canClick && !hasTextSelection()) {
              updateUnsyncedQueryParams({ modalId: id });
            }
          }}
          mb={3}
          pt={{ _: imageGallery?.length > 0 ? 0 : 3, sm: 3 }}
          css={`
            &:hover {
              .slider-navigation {
                opacity: 0.8;
              }
            }
          `}
        >
          <Stack
            gap={3}
            flexDirection={{ _: "column-reverse", sm: "row" }}
            justifyContent="space-between"
            flex={1}
            alignItems={{ _: "normal", sm: "flex-start" }}
          >
            <Stack direction="vertical" gap={1}>
              <Text
                variant={isPOSRelease ? "header.S" : "header.M"}
                display="flex"
                alignItems="center"
              >
                {title}
                {isPOSRelease && canClick && (
                  <Icon
                    icon={<Info size={16} weight="light" />}
                    ml={1}
                    cursor={"pointer"}
                    onClick={() => {
                      if (canClick && !hasTextSelection()) {
                        updateUnsyncedQueryParams({ modalId: id });
                      }
                    }}
                  />
                )}
              </Text>
              {!isPOSRelease && (
                <TextPreviewRender value={description} lines={3} maxW={"90%"} />
              )}
            </Stack>

            {imageGallery?.length > 0 && !isPOSRelease && (
              <Box ml={{ _: -2, sm: 0 } as any} mr={{ _: -2, sm: 0 } as any}>
                <ImageSlider
                  imageGallery={imageGallery}
                  aspectRatio="5 / 2"
                  width={{ _: "100%", sm: 360 }}
                  borderRadius={{ sm: "sm" }}
                  imageProps={{
                    objectFit: "cover",
                    sizes: `
                      (max-width: ${theme.breakpoints.sm768}) 100vw, 
                      (max-width: ${theme.breakpoints.md1024}) 50vw, 
                      30vw
                    `,
                  }}
                  navigationSize="sm"
                />
              </Box>
            )}
          </Stack>
        </Box>
      );
    }

    return (
      <Box>
        <LegacyText
          color="N800"
          fontWeight="heavy"
          fontSize={{ _: 6, md: 7 }}
          textAlign={{ _: "left", md: "left" }}
          lineHeight="normal"
          mb={2}
        >
          {title}
        </LegacyText>
        {description && (
          <Box
            w="100%"
            p={2}
            bg="N100"
            borderRadius="sm"
            border="1px"
            borderColor="N300"
            mb={2}
          >
            <PlainTextRender value={description} />
          </Box>
        )}
      </Box>
    );
  })();

  return (
    <>
      {isExperienceSales && <Box ref={headSpacerRef} pt={isFirst ? 3 : 8} />}
      <Box id={id} mb={isLast ? 8 : isExperienceSales ? 0 : 6}>
        {title && TitleComponent}
        {children}
        <DetailsModal
          id={id}
          imageGallery={imageGallery}
          aspectRatio={"5 / 2"}
          title={title}
          description={description}
          stickySection={({ isSticky }) => (
            <Text
              as="h1"
              variant="header.M"
              px={isSticky ? { _: 2, sm: 4 } : 0}
            >
              {title}
            </Text>
          )}
        />
      </Box>
    </>
  );
}

function ZoneList({
  zones,
  addons,
  releaseType,
  requiresAuth,
  selectedTickets,
  selectedAddons,
  showWaitlistMessage,
  handleChange,
  showHiddenFees,
  SoldOutWaitlistMessage,
  isExperienceSales,
  eventId,
  isMobile,
}: {
  handleChange: (value: number, id: string, zoneId: string) => void;
  zones: IReleaseZone[];
  addons: IReleaseTicket[];
  isExperienceSales: boolean;
  isAdmin: boolean;
  requiresAuth: boolean;
  hasResaleWaitlist: boolean;
  selectedTickets: IReleaseTicket[];
  selectedAddons: IReleaseTicket[];
  releaseType: ReleaseType;
  showHiddenFees: boolean;
  ticketsSoldForUser:
    | TicketsSoldForUserQuery["ticketsSoldForUser"]
    | MembershipsSoldForUserQuery["membershipsSoldForUser"];
  SoldOutWaitlistMessage: TSoldOutWaitlistMessage;
  showWaitlistMessage: boolean;
  onClickZone: (id: string) => void;
  isMobile: boolean;
  eventId?: string;
}) {
  const { hasFeature } = useOrganization();
  const isExperienceAccordion = hasFeature("ticketAccordion");

  const isPOSRelease = releaseType === ReleaseType.Pos;

  // Identify sold-out zones
  let soldOutZones = zones?.filter(
    (zone) =>
      zone.ticketTypes?.length > 0 &&
      zone.ticketTypes
        ?.filter((tt) => tt.isActive)
        ?.every((tt) => tt.isSoldOut ?? false)
  );

  // Hack: Big Sky PBR 2025 (Outlaw). Remove 2025-08-01
  // Show waitlist message if at least one ticket type is sold out
  const isBigSkyHack = eventId === "2528b62f-2a71-49f8-ac56-523f63152b72";
  if (isBigSkyHack) {
    soldOutZones = zones?.filter((zone) =>
      zone.ticketTypes
        ?.filter((tt) => tt.isActive)
        ?.some((tt) => tt.isSoldOut ?? false)
    );
  }

  // If the feature is on, first zone is open by default; otherwise everything is open
  const [openZoneIds, setOpenZoneIds] = useState<string[]>(
    isExperienceAccordion && zones?.length ? [zones[0].id] : []
  );

  const toggleZone = (zoneId: string) => {
    if (!isExperienceAccordion) return;

    setOpenZoneIds((prev) =>
      prev.includes(zoneId)
        ? prev.filter((id) => id !== zoneId)
        : [...prev, zoneId]
    );
  };

  return (
    <>
      {zones?.map((zone, zoneIdx) => {
        const isOpen = isExperienceAccordion
          ? openZoneIds.includes(zone.id)
          : true;

        const ticketListItems = zone.ticketTypes.map((ticketType, idx) => {
          let selectedQuantity: IReleaseTicket | undefined;
          switch (ticketType.type) {
            case "addon":
              selectedQuantity = selectedAddons?.find(
                (addon) => addon.id === ticketType.id
              );
              break;
            case "ticket":
              selectedQuantity = selectedTickets?.find(
                (seat) =>
                  seat.id === ticketType.id && seat.zoneName === zone?.name
              );
              break;
            default:
              break;
          }

          return (
            <ListItem
              zoneId={zone.id}
              key={ticketType.id}
              ticketType={ticketType}
              showHiddenFees={showHiddenFees}
              handleChange={handleChange}
              quantity={selectedQuantity?.quantity}
              isFirstItem={idx === 0}
              isLastItem={idx === zone.ticketTypes?.length - 1}
              isPOSRelease={isPOSRelease}
            />
          );
        });

        const soldOutZone = soldOutZones?.find((z) => z.id === zone.id);
        const preventWaitlistJoining =
          (requiresAuth && releaseType === ReleaseType.Unlisted) ||
          releaseType === ReleaseType.Pos ||
          releaseType === ReleaseType.Outlet;

        const ticketCount = zone.ticketTypes?.length ?? 0;
        const isSingular = ticketCount === 1;

        return (
          <Box key={zone.id} mb={4}>
            <Group
              id={zone.id}
              isExperienceSales={isExperienceSales}
              title={zone.name}
              description={zone.description}
              imageGallery={zone.imageGallery}
              hasTicketTypes={ticketListItems.length > 0}
              isLast={!addons?.length && zoneIdx === zones.length - 1}
              isFirst={zoneIdx === 0}
              isPOSRelease={isPOSRelease}
            />

            {ticketListItems.length > 0 && (
              <Box mt={0}>
                {isExperienceAccordion && (
                  <Button
                    variant="solid"
                    isRounded
                    size="md"
                    onClick={() => toggleZone(zone.id)}
                  >
                    {isOpen
                      ? `Hide ${ticketCount} ${
                          isSingular ? "option" : "options"
                        }`
                      : `Show ${ticketCount} ${
                          isSingular ? "option" : "options"
                        }`}
                  </Button>
                )}

                {isOpen && (
                  <Box mt={2}>
                    {!isExperienceSales && <Divider my={{ _: 2, md: 2 }} />}
                    <Box>{ticketListItems}</Box>

                    {showWaitlistMessage &&
                      soldOutZone &&
                      !preventWaitlistJoining && (
                        <>
                          {!isExperienceSales && (
                            <Divider my={{ _: 2, md: 2 }} />
                          )}
                          <SoldOutWaitlistMessage
                            zoneName={
                              isBigSkyHack
                                ? soldOutZone.ticketTypes
                                    .filter((t) => t.isActive && t.isSoldOut)
                                    .map((t) => t.name)
                                    .join(", ")
                                : soldOutZone.name
                            }
                          />
                        </>
                      )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </>
  );
}

function DelayedSubmissionMessage() {
  return (
    <Box mx={"1/2"} my={20} px={4}>
      <Spinner size={64} />
      <LegacyText
        mt={4}
        fontSize={4}
        lineHeight="high"
        color="N600"
        textAlign="center"
      >
        We&apos;re just double-checking that you&apos;re not a ticket-buying
        robot in disguise.
      </LegacyText>
      <LegacyText
        mt={1}
        fontSize={4}
        lineHeight="high"
        color="N600"
        textAlign="center"
      >
        Your tickets are nearly ready.
      </LegacyText>
      <LegacyText
        textAlign="center"
        mt={4}
        fontSize={4}
        lineHeight="high"
        color="N600"
        fontWeight={"heavy"}
      >
        Don&apos;t refresh this page.
      </LegacyText>
    </Box>
  );
}

interface IPaymentOptions {
  hasTicketSelected: boolean;
  userCredits: number;
  user: any;
  paymentPlanSettings: EventQuery["event"]["paymentPlanSettings"];
  isSubmitting: boolean;
  primaryButtonLabel: string;
  showHiddenFees: boolean;
  orgCurrency: string;
  isMobile: boolean;
  onOrderCreationClick: any;
  isExperienceSales: boolean;
  showGateways: boolean;
  showUsedCredits: boolean;
  cart: Cart;
}

function PaymentOptions(props: IPaymentOptions) {
  return props.isExperienceSales
    ? ExperienceSalesPaymentOptions(props)
    : NonSeatedPaymentOptions(props);
}

function ExperienceSalesPaymentOptions(props: IPaymentOptions) {
  const {
    hasTicketSelected,
    userCredits,
    user,
    orgCurrency,
    isMobile,
    showGateways,
    showUsedCredits,
    paymentPlanSettings,
    cart,
  } = props;

  const { organization } = useOrganization();

  return (
    <>
      {cart.multibuyDiscount > 0 && (
        <>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text variant="regular">Multibuy promotion applied</Text>
            <Text variant="regular">
              <Price
                price={-cart.multibuyDiscount}
                currency={organization.currency}
              />
            </Text>
          </Flex>
          <Divider my={{ _: 3, md: 2 }} />
        </>
      )}

      {cart.referralDiscount > 0 && (
        <>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text variant="regular">Referral discount applied</Text>
            <Text variant="regular">
              <Price
                price={-cart.referralDiscount}
                currency={organization.currency}
              />
            </Text>
          </Flex>
          <Divider my={{ _: 3, md: 2 }} />
        </>
      )}

      {showUsedCredits && (
        <>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text variant="regular">Your credits to be deducted</Text>
            <Text variant="regular">{`- ${formatPrice(userCredits, {
              currency: organization?.currency,
            })}`}</Text>
          </Flex>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text variant="regular">
              Total left to pay after deducting credits
            </Text>
            <Text variant="regular">
              {cart.total - userCredits > 0 ? (
                <Price
                  price={cart.total - userCredits}
                  currency={organization.currency}
                />
              ) : (
                "$0.00"
              )}
            </Text>
          </Flex>
          <Divider my={{ _: 3, md: 2 }} />
        </>
      )}

      {showGateways && (
        <>
          {cart.afterPay > 0 && (
            <Box mt={2} w="100%">
              <BuyNowPayLater
                totalPrice={cart.afterPay - userCredits}
                hasTicketSelected={hasTicketSelected}
                isSmallDisplay={isMobile}
                bnplIntegration={{
                  type: IntegrationType.PaymentAfterpay,
                }}
              />
            </Box>
          )}
          {cart.laybuy > 0 && (
            <Box mt={2} w="100%">
              <BuyNowPayLater
                totalPrice={cart.laybuy - userCredits}
                hasTicketSelected={hasTicketSelected}
                isSmallDisplay={isMobile}
                bnplIntegration={{
                  type: IntegrationType.PaymentLaybuy,
                }}
              />
            </Box>
          )}
          {cart.stripeAfterPay > 0 && (
            <Box mt={2}>
              <AfterpayTeaser
                locale={getAfterPayLocaleByCurrency(orgCurrency)}
                currency={orgCurrency ?? "NZD"}
                amount={cart.stripeAfterPay - userCredits}
              />
            </Box>
          )}
          {paymentPlanSettings && !paymentPlanSettings.disabledAt && (
            <PaymentPlanBadge
              maxInstallmentCount={paymentPlanSettings?.scheduleOptions
                ?.map((s) => s?.installmentCount)
                ?.reduce((a, b) => Math.max(a, b))}
            />
          )}
        </>
      )}
      <Box mb={8} />
    </>
  );
}

function NonSeatedPaymentOptions(props: IPaymentOptions) {
  const {
    hasTicketSelected,
    userCredits,
    user,
    showHiddenFees,
    orgCurrency,
    isMobile,
    onOrderCreationClick,
    primaryButtonLabel,
    isSubmitting,
    showGateways,
    showUsedCredits,
    paymentPlanSettings,
    cart,
  } = props;
  const { organization } = useOrganization();
  return (
    <>
      <Divider mt={{ _: 3, md: 0 }} />

      {cart.multibuyDiscount > 0 && (
        <>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <LegacyText fontWeight="medium" fontSize={2} color="N600">
              Multibuy promotion applied
            </LegacyText>
            <Price
              price={-cart.multibuyDiscount}
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
              currency={organization.currency}
            />
          </Flex>
          <Divider my={{ _: 3, md: 2 }} />
        </>
      )}

      {cart.referralDiscount > 0 && (
        <>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <LegacyText fontWeight="medium" fontSize={2} color="N600">
              Referral discount applied
            </LegacyText>
            <Price
              price={-cart.referralDiscount}
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
              currency={organization.currency}
            />
          </Flex>
          <Divider my={{ _: 3, md: 2 }} />
        </>
      )}

      <Flex mt={2} justifyContent="space-between" w="100%">
        <LegacyText fontWeight="medium" fontSize={2} color="N600">
          Total {showHiddenFees ? "(including booking fees and taxes)" : ""}
        </LegacyText>
        <LegacyText
          fontWeight="medium"
          fontSize={4}
          lineHeight={"18px" as any}
          color={"black" as any}
        >
          {hasTicketSelected ? (
            <Price price={cart.total} currency={organization.currency} />
          ) : (
            "-"
          )}
        </LegacyText>
      </Flex>

      {showUsedCredits && (
        <>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <LegacyText fontWeight="medium" fontSize={2} color="N600">
              Your credits to be deducted
            </LegacyText>
            <LegacyText
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {`- ${formatPrice(userCredits, {
                currency: organization?.currency,
              })}`}
            </LegacyText>
          </Flex>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <LegacyText fontWeight="medium" fontSize={2} color="N600">
              Total left to pay after deducting credits
            </LegacyText>
            <LegacyText
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {cart.total - userCredits > 0 ? (
                <Price
                  price={cart.total - userCredits}
                  currency={organization.currency}
                />
              ) : (
                "$0.00"
              )}
            </LegacyText>
          </Flex>
        </>
      )}

      {showGateways && (
        <>
          {cart.afterPay > 0 && (
            <Box mt={2} w="100%">
              <BuyNowPayLater
                totalPrice={cart.afterPay - userCredits}
                hasTicketSelected={hasTicketSelected}
                isSmallDisplay={isMobile}
                bnplIntegration={{
                  type: IntegrationType.PaymentAfterpay,
                }}
              />
            </Box>
          )}
          {cart.laybuy > 0 && (
            <Box mt={2} w="100%">
              <BuyNowPayLater
                totalPrice={cart.laybuy - userCredits}
                hasTicketSelected={hasTicketSelected}
                isSmallDisplay={isMobile}
                bnplIntegration={{
                  type: IntegrationType.PaymentLaybuy,
                }}
              />
            </Box>
          )}
          {cart.stripeAfterPay > 0 && (
            <AfterpayTeaser
              locale={getAfterPayLocaleByCurrency(orgCurrency)}
              currency={orgCurrency ?? "NZD"}
              amount={cart.stripeAfterPay - userCredits}
            />
          )}
          {
            <PaymentPlanBadge
              maxInstallmentCount={paymentPlanSettings?.scheduleOptions
                ?.map((s) => s?.installmentCount)
                ?.reduce((a, b) => Math.max(a, b))}
            />
          }
        </>
      )}

      <PrimaryButton
        mt={{ _: 2, md: 6 }}
        padding="mobileGutter"
        w="100%"
        onClick={onOrderCreationClick}
        isLoading={isSubmitting}
        disabled={hasTicketSelected === false}
      >
        {primaryButtonLabel}
      </PrimaryButton>
    </>
  );
}
